/**
 * Durable offline cache for Compás boards (IndexedDB).
 * Stores board JSON snapshots, asset blobs, and pending PATCH payloads.
 */
import {
  isOfflineEligibleBoardKind,
  mergePendingIntoSnapshot,
} from "./moodboardOfflinePure";

const DB_NAME = "compas-offline-v1";
const DB_VERSION = 1;
const STORE_BOARDS = "boards";
const STORE_ASSETS = "assets";

let dbPromise = null;

function openDb() {
  if (typeof indexedDB === "undefined") {
    return Promise.reject(new Error("IndexedDB no disponible"));
  }
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onerror = () => reject(req.error || new Error("IDB open failed"));
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_BOARDS)) {
        db.createObjectStore(STORE_BOARDS, { keyPath: "id" });
      }
      if (!db.objectStoreNames.contains(STORE_ASSETS)) {
        const assets = db.createObjectStore(STORE_ASSETS, {
          keyPath: "assetId",
        });
        assets.createIndex("boardId", "boardId", { unique: false });
      }
    };
    req.onsuccess = () => resolve(req.result);
  });
  return dbPromise;
}

function idbReq(req) {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error || new Error("IDB request failed"));
  });
}

function txDone(tx) {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error || new Error("IDB tx failed"));
    tx.onabort = () => reject(tx.error || new Error("IDB tx aborted"));
  });
}

export async function getOfflineBoardRecord(boardId) {
  const id = Number(boardId);
  if (!Number.isFinite(id) || id <= 0) return null;
  try {
    const db = await openDb();
    return (
      (await idbReq(
        db.transaction(STORE_BOARDS).objectStore(STORE_BOARDS).get(id),
      )) || null
    );
  } catch {
    return null;
  }
}

export async function listOfflineBoardIds() {
  try {
    const db = await openDb();
    const rows = await idbReq(
      db.transaction(STORE_BOARDS).objectStore(STORE_BOARDS).getAll(),
    );
    return (rows || [])
      .filter((r) => r?.offlineEnabled)
      .map((r) => Number(r.id))
      .filter((n) => Number.isFinite(n));
  } catch {
    return [];
  }
}

export async function isBoardOfflineEnabled(boardId) {
  const row = await getOfflineBoardRecord(boardId);
  return Boolean(row?.offlineEnabled);
}

export async function hasOfflinePending(boardId) {
  const row = await getOfflineBoardRecord(boardId);
  return Boolean(row?.pendingPages || row?.pendingMeta);
}

export async function getOfflineBoardSnapshot(boardId) {
  const row = await getOfflineBoardRecord(boardId);
  if (!row?.snapshot) return null;
  return mergePendingIntoSnapshot(
    row.snapshot,
    row.pendingPages,
    row.pendingMeta,
  );
}

export async function putOfflineBoardSnapshot(
  boardId,
  snapshot,
  { offlineEnabled = true } = {},
) {
  const id = Number(boardId);
  if (!Number.isFinite(id) || id <= 0) throw new Error("boardId inválido");
  const db = await openDb();
  const prev = await idbReq(
    db.transaction(STORE_BOARDS).objectStore(STORE_BOARDS).get(id),
  );
  const snapAssets = Array.isArray(snapshot?.assets) ? snapshot.assets : null;
  const mergedSnapshot =
    prev?.snapshot &&
    (!snapAssets || !snapAssets.length) &&
    prev.snapshot.assets
      ? {
          ...snapshot,
          assets: prev.snapshot.assets,
          Kind: snapshot?.Kind || prev.snapshot.Kind,
        }
      : snapshot;
  const kind =
    mergedSnapshot?.Kind ||
    mergedSnapshot?.kind ||
    prev?.snapshot?.Kind ||
    prev?.snapshot?.kind;
  if (!isOfflineEligibleBoardKind(kind)) {
    throw new Error("Este tipo de board no admite offline");
  }
  const record = {
    id,
    offlineEnabled: offlineEnabled !== false,
    snapshot: mergedSnapshot,
    expectedUpdatedAt:
      mergedSnapshot?.updatedAt || prev?.expectedUpdatedAt || null,
    pendingPages: prev?.pendingPages || null,
    pendingMeta: prev?.pendingMeta || null,
    assetIds: snapAssets?.length
      ? snapAssets.map((a) => Number(a.Id || a.id)).filter(Number.isFinite)
      : prev?.assetIds || [],
    cachedAt: new Date().toISOString(),
  };
  const tx = db.transaction(STORE_BOARDS, "readwrite");
  tx.objectStore(STORE_BOARDS).put(record);
  await txDone(tx);
  return record;
}

export async function queueOfflinePages(boardId, pendingPages) {
  const id = Number(boardId);
  const db = await openDb();
  const tx = db.transaction(STORE_BOARDS, "readwrite");
  const store = tx.objectStore(STORE_BOARDS);
  const prev = (await idbReq(store.get(id))) || {
    id,
    offlineEnabled: true,
    snapshot: null,
    pendingMeta: null,
    assetIds: [],
  };
  store.put({
    ...prev,
    pendingPages,
    cachedAt: new Date().toISOString(),
  });
  await txDone(tx);
}

export async function queueOfflineMeta(boardId, pendingMeta) {
  const id = Number(boardId);
  const db = await openDb();
  const tx = db.transaction(STORE_BOARDS, "readwrite");
  const store = tx.objectStore(STORE_BOARDS);
  const prev = (await idbReq(store.get(id))) || {
    id,
    offlineEnabled: true,
    snapshot: null,
    pendingPages: null,
    assetIds: [],
  };
  store.put({
    ...prev,
    pendingMeta,
    cachedAt: new Date().toISOString(),
  });
  await txDone(tx);
}

export async function clearOfflinePending(
  boardId,
  { pages = true, meta = true, expectedUpdatedAt } = {},
) {
  const id = Number(boardId);
  const row = await getOfflineBoardRecord(id);
  if (!row) return;
  const db = await openDb();
  const tx = db.transaction(STORE_BOARDS, "readwrite");
  tx.objectStore(STORE_BOARDS).put({
    ...row,
    pendingPages: pages ? null : row.pendingPages,
    pendingMeta: meta ? null : row.pendingMeta,
    expectedUpdatedAt:
      expectedUpdatedAt !== undefined
        ? expectedUpdatedAt
        : row.expectedUpdatedAt,
    cachedAt: new Date().toISOString(),
  });
  await txDone(tx);
}

export async function putOfflineAssetBlob(boardId, assetId, blob) {
  const bid = Number(boardId);
  const aid = Number(assetId);
  if (!Number.isFinite(bid) || !Number.isFinite(aid) || !blob) return;
  const db = await openDb();
  const tx = db.transaction(STORE_ASSETS, "readwrite");
  tx.objectStore(STORE_ASSETS).put({
    assetId: aid,
    boardId: bid,
    blob,
    mimeType: blob.type || "application/octet-stream",
    cachedAt: new Date().toISOString(),
  });
  await txDone(tx);
}

export async function getOfflineAssetBlob(assetId) {
  const aid = Number(assetId);
  if (!Number.isFinite(aid)) return null;
  try {
    const db = await openDb();
    const row = await idbReq(
      db.transaction(STORE_ASSETS).objectStore(STORE_ASSETS).get(aid),
    );
    return row?.blob || null;
  } catch {
    return null;
  }
}

export async function getOfflineAssetBlobUrl(assetId) {
  const blob = await getOfflineAssetBlob(assetId);
  if (!blob) return "";
  return URL.createObjectURL(blob);
}

export async function removeOfflineBoard(boardId) {
  const id = Number(boardId);
  if (!Number.isFinite(id)) return;
  const db = await openDb();
  const row = await idbReq(
    db.transaction(STORE_BOARDS).objectStore(STORE_BOARDS).get(id),
  );
  const assetIds = row?.assetIds || [];
  const tx = db.transaction([STORE_BOARDS, STORE_ASSETS], "readwrite");
  tx.objectStore(STORE_BOARDS).delete(id);
  const assets = tx.objectStore(STORE_ASSETS);
  for (const aid of assetIds) {
    assets.delete(Number(aid));
  }
  await txDone(tx);
}

/**
 * Prefetch board snapshot + asset blobs for offline use.
 * fetchBoard(id) → GET snapshot
 * fetchAssetBlob(assetId) → Blob (not object URL)
 */
export async function prefetchBoardForOffline(
  boardId,
  { fetchBoard, fetchAssetBlob, onProgress } = {},
) {
  if (typeof fetchBoard !== "function") {
    throw new Error("fetchBoard requerido");
  }
  const snapshot = await fetchBoard(boardId);
  if (!isOfflineEligibleBoardKind(snapshot?.Kind || snapshot?.kind)) {
    throw new Error("Este tipo de board no admite offline");
  }
  await putOfflineBoardSnapshot(boardId, snapshot, { offlineEnabled: true });

  const assets = Array.isArray(snapshot?.assets) ? snapshot.assets : [];
  const total = assets.length;
  let done = 0;
  onProgress?.({ done, total, phase: "assets" });

  if (typeof fetchAssetBlob === "function") {
    for (const a of assets) {
      const aid = Number(a.Id || a.id);
      if (!Number.isFinite(aid)) {
        done += 1;
        onProgress?.({ done, total, phase: "assets" });
        continue;
      }
      try {
        const blob = await fetchAssetBlob(aid);
        if (blob) await putOfflineAssetBlob(boardId, aid, blob);
      } catch {
        /* keep going — layout still useful without every asset */
      }
      done += 1;
      onProgress?.({ done, total, phase: "assets" });
    }
  }

  onProgress?.({ done: total, total, phase: "done" });
  return snapshot;
}

/**
 * Flush pending pages/meta to the API.
 * savePages(id, pages, { expectedUpdatedAt, pasteboard })
 * updateMeta(id, payload with ExpectedUpdatedAt)
 */
export async function flushOfflineBoard(
  boardId,
  { savePages, updateMeta, onConflict } = {},
) {
  const row = await getOfflineBoardRecord(boardId);
  if (!row) return { ok: true, flushed: false };
  if (!row.pendingPages && !row.pendingMeta) {
    return { ok: true, flushed: false };
  }

  let lastUpdatedAt = row.expectedUpdatedAt;

  if (row.pendingPages && typeof savePages === "function") {
    try {
      const data = await savePages(boardId, row.pendingPages.pages, {
        expectedUpdatedAt: row.pendingPages.expectedUpdatedAt || lastUpdatedAt,
        pasteboard: row.pendingPages.pasteboard,
      });
      lastUpdatedAt = data?.updatedAt || lastUpdatedAt;
      await putOfflineBoardSnapshot(boardId, data, { offlineEnabled: true });
      await clearOfflinePending(boardId, {
        pages: true,
        meta: false,
        expectedUpdatedAt: lastUpdatedAt,
      });
    } catch (err) {
      if (err?.response?.status === 409) {
        onConflict?.(err);
        return { ok: false, conflict: true, error: err };
      }
      throw err;
    }
  }

  const fresh = await getOfflineBoardRecord(boardId);
  if (fresh?.pendingMeta && typeof updateMeta === "function") {
    try {
      const m = fresh.pendingMeta;
      const data = await updateMeta(boardId, {
        Titulo: m.Titulo,
        CategoryLabel: m.CategoryLabel,
        StudioLine: m.StudioLine,
        ChromeStyleJson: m.ChromeStyleJson,
        ExpectedUpdatedAt: m.expectedUpdatedAt || lastUpdatedAt,
      });
      lastUpdatedAt = data?.updatedAt || lastUpdatedAt;
      await putOfflineBoardSnapshot(
        boardId,
        { ...(fresh.snapshot || {}), ...data },
        { offlineEnabled: true },
      );
      await clearOfflinePending(boardId, {
        pages: false,
        meta: true,
        expectedUpdatedAt: lastUpdatedAt,
      });
    } catch (err) {
      if (err?.response?.status === 409) {
        onConflict?.(err);
        return { ok: false, conflict: true, error: err };
      }
      throw err;
    }
  }

  return { ok: true, flushed: true, updatedAt: lastUpdatedAt };
}
